export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export const ALLOWED_ATTACHMENT_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
  'video/mp4',
  'video/quicktime',
]);

export const ATTACHMENT_ACCEPT = Array.from(ALLOWED_ATTACHMENT_TYPES).join(',');

export interface AttachmentFile {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  downloadUrl?: string;
}

export function normalizeAttachmentFile(raw: Record<string, unknown>): AttachmentFile {
  return {
    id: String(raw.id ?? raw._id ?? ''),
    fileName: String(raw.fileName ?? raw.filename ?? 'Attachment'),
    mimeType: String(raw.mimeType ?? raw.contentType ?? 'application/octet-stream'),
    fileSize: Number(raw.fileSize ?? 0),
    downloadUrl: raw.downloadUrl ? String(raw.downloadUrl) : undefined,
  };
}

export function isPreviewableAttachment(att: Pick<AttachmentFile, 'mimeType'>) {
  return att.mimeType.startsWith('image/');
}

// Browser-renderable raster formats. HEIC/HEIF (and other non-renderable image
// types) are excluded — most browsers can't paint them, so an <img> preview
// would show a broken thumbnail; those fall back to a generic icon instead.
export const RENDERABLE_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
]);

export function isRenderableImage(mimeType: string) {
  return RENDERABLE_IMAGE_TYPES.has(mimeType.toLowerCase());
}

export function validateAttachmentFile(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return `${file.name} exceeds the 25 MB limit.`;
  if (!ALLOWED_ATTACHMENT_TYPES.has(file.type)) {
    return `${file.name}: unsupported file type.`;
  }
  return null;
}

export function formatAttachmentSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
